import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const MAX_CONTEXT_LENGTH = 500;

export default function AnalysisInputModal({
  open,
  onClose,
  onSubmit,
  isAnalyzing,
}) {
  const [value, setValue] = useState("");

  useEffect(() => {
    if (open) {
      setValue("");
    }
  }, [open]);

  const trimmedValue = useMemo(() => value.trim(), [value]);
  const canSubmitWithContext = trimmedValue.length > 0;

  const handleSubmit = (useContext) => {
    const context = useContext ? trimmedValue : null;
    onSubmit?.(context);
    onClose?.();
  };

  const handleKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      handleSubmit(canSubmitWithContext);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (!nextOpen ? onClose?.() : null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Analysis Context (Optional)</DialogTitle>
          <DialogDescription>
            Share your opinion or concerns to help the analysis focus on key defects.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Textarea
              value={value}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe what you're concerned about, known issues, or specific areas to focus on..."
              maxLength={MAX_CONTEXT_LENGTH}
              rows={4}
              autoFocus
            />
            <div className="text-xs text-slate-500 text-right">
              {value.length}/{MAX_CONTEXT_LENGTH}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleSubmit(false)}
              disabled={isAnalyzing}
            >
              Skip & Analyze
            </Button>
            <Button
              type="button"
              className="bg-violet-600 hover:bg-violet-700"
              onClick={() => handleSubmit(true)}
              disabled={!canSubmitWithContext || isAnalyzing}
            >
              Analyze with Context
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
